import Decimal from 'decimal.js';
import { compact } from 'lodash';

import { FUNCTIONAL } from '@/styles/tokens/colors';
import { formatCurrencyNoDecimals } from '@/utils/formatting/currency';

import { EstateWaterfall_TransferLogItemFragment } from '../../../graphql/EstateWaterfall.generated';
import { EdgeLabelCard } from '../edgeLabelCard.component';
import { CardConfig } from './types';

interface CrtFundingCardParams {
  sourceEntityName: string;
  targetEntityName: string;
  transfer?: EstateWaterfall_TransferLogItemFragment;
}

export function createCrtFundingCard({
  sourceEntityName,
  targetEntityName,
  transfer,
}: CrtFundingCardParams): CardConfig {
  const grantor = sourceEntityName;
  const crtName = targetEntityName;

  const isHypothetical = transfer?.isHypothetical;

  // Get transfer amount
  const fundingAmount = transfer?.finalTransferAmount || new Decimal(0);

  const term = (() => {
    if (transfer?.associatedObject?.__typename === 'HypotheticalCRT') {
      const numYears =
        transfer.associatedObject.simulatedCrt.crtProposal?.termYears;
      return numYears ? `${numYears} years` : null;
    }

    return null;
  })();

  const termEndYear = (() => {
    if (transfer?.associatedObject?.__typename === 'HypotheticalCRT') {
      const numYears =
        transfer.associatedObject.simulatedCrt.crtProposal?.termYears;
      return numYears ? new Date().getFullYear() + numYears : null;
    }

    if (
      transfer?.associatedObject?.__typename === 'Entity' &&
      transfer.associatedObject.subtype.__typename === 'CRTTrust'
    ) {
      return transfer.associatedObject.subtype.termEndDate?.getFullYear();
    }

    return null;
  })();

  return {
    element: (
      <EdgeLabelCard
        key={`crt-funding-${sourceEntityName}-${targetEntityName}`}
        title={isHypothetical ? 'Hypothetical CRT funding' : 'CRT funding'}
        titleSx={
          isHypothetical ? { color: FUNCTIONAL.WARNING[600] } : undefined
        }
        description={`${grantor} transfers assets to ${crtName}.`}
        rows={compact([
          {
            label: 'Funding amount',
            value: formatCurrencyNoDecimals(fundingAmount),
          },
          term
            ? {
                label: 'CRT term',
                value: term,
              }
            : null,
          termEndYear
            ? {
                label: 'Term end',
                value: termEndYear.toString(),
              }
            : null,
        ])}
      />
    ),
    label: 'CRT funding',
    value: formatCurrencyNoDecimals(fundingAmount),
    variant: isHypothetical ? 'secondary' : 'primary',
  };
}
